import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {CurveNode} from "#template/nodes/object/curve/curve-node"
import type {ObjectNodeDefinition, ObjectNodeParameters} from "#template/nodes/object/types"
import {objectNodeParameters} from "#template/nodes/object/validation"
import type {CurveData} from "#template/nodes/object/curve/types"
import type {OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import {curveGeometryAttributeSetLike} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import type {CurveGeometryAttributeSetLikeNode} from "#template/nodes/geometry-graph/attribute-set/attribute-set-node"
import {GenerateGeometry} from "#template/runtime-graph/nodes/generate-geometry"
import {Matrix4} from "@cm/math"
import {hashObject, graphToJson} from "@cm/utils"
import {NoopLogger} from "@cm/log"
import type {ReifiedGeometryData, ReifiedGeometryAttributeData, ReifiedGeometryAttributeSetData} from "#template/interfaces/geometry-data"
import type {AbstractCurveData, CompleteCurveData, ReifiedCurveData} from "#template/interfaces/curve-data"
import type {ValueType} from "#template/geometry-processing/geometry-graph"
import {elementCountAndTypeFromValueType, geomToCurve} from "#template/geometry-processing/geometry-graph"
import type {StandardCurveGeometryAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import {isStandardCurveAttributeSetData} from "#template/nodes/geometry-graph/interfaces"
import type {ExprsTreeTokenSet, ArrayForAttributeValueType} from "#template/nodes/geometry-graph/common/geometry-graph-evaluation"
import {
    geometryAttributeSetDataToGeometryAttributeRefDataSet,
    attributeRefSetFromGeometryAttributeRefDataSet,
    collectDisplayGraphResourcesFromGeometryAttributeRefDataSet,
} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"
import {
    setupGeometryGenerationData,
    reifiedGeometryDataToReifiedGeometryAttributeSetData,
    isExpectedReifiedGeometryAttributeData,
} from "#template/nodes/geometry-graph/common/geometry-graph-evaluation"
import type {SceneNodes} from "#template/interfaces/scene-object"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import {EMPTY_LIST} from "#template/runtime-graph/types"
import {visitNone} from "#template/template-node/visit-helpers"

const TD = TypeDescriptors

const sceneNodeArrayDesc = TD.Array(TD.Identity<SceneNodes.SceneNode>())
const abstractCurveDataDesc = TD.Identity<AbstractCurveData>("contentHash")
const reifiedCurveDataDesc = TD.Identity<ReifiedCurveData>("contentHash")

function reifiedGeometryDataToReifiedCurveData<T extends ExprsTreeTokenSet>(reifiedGeometryData: ReifiedGeometryData, exprsTreeTokenSet: T): ReifiedCurveData {
    const reifiedAttributeSet = reifiedGeometryDataToReifiedGeometryAttributeSetData(reifiedGeometryData, exprsTreeTokenSet, 1)

    const curveAttributeDataArrayToCurveDataArray = <T extends ValueType>(
        attributeArray: ArrayForAttributeValueType<T>,
        attributeValueType: T,
    ): ArrayForAttributeValueType<T> => {
        const {count: valueTypeDim} = elementCountAndTypeFromValueType(attributeValueType)

        const segmentsCount = attributeArray.length / 2 / valueTypeDim
        const buffer: number[] = []

        const pushSegmentVertex = (segmentIndex: number, vertexIdx: 0 | 1) => {
            const offset = segmentIndex * valueTypeDim * 2 + vertexIdx * valueTypeDim
            for (let i = 0; i < valueTypeDim; i++) buffer.push(attributeArray[offset + i])
        }

        for (let segmentIndex = 0; segmentIndex < segmentsCount; segmentIndex++) {
            pushSegmentVertex(segmentIndex, 0)
        }
        pushSegmentVertex(segmentsCount - 1, 1)

        return (attributeValueType === "uint32" ? new Uint32Array(buffer) : new Float32Array(buffer)) as ArrayForAttributeValueType<T>
    }

    const parseAttributeData = <T extends ValueType>(
        reifiedGeometryData: ReifiedGeometryAttributeSetData | ReifiedGeometryAttributeData,
        expectedAttributeValueType: T,
    ): ArrayForAttributeValueType<T> => {
        if (!isExpectedReifiedGeometryAttributeData(reifiedGeometryData, 1, expectedAttributeValueType))
            throw new Error(`Invalid reified geometry attribute data: ${JSON.stringify(reifiedGeometryData)}`)

        const dataArray = reifiedGeometryData.data
        if (dataArray instanceof Float32Array) {
            return curveAttributeDataArrayToCurveDataArray(dataArray, expectedAttributeValueType) as ArrayForAttributeValueType<T>
        } else if (dataArray instanceof Uint32Array) {
            return curveAttributeDataArrayToCurveDataArray(dataArray, expectedAttributeValueType) as ArrayForAttributeValueType<T>
        } else {
            throw new Error(`Invalid reified geometry attribute data type: ${typeof dataArray}, expected ${expectedAttributeValueType}`)
        }
    }

    return {
        contentHash: reifiedGeometryData.contentHash,
        points: parseAttributeData(reifiedAttributeSet.points, "float3"),
        normals: parseAttributeData(reifiedAttributeSet.normals, "float3"),
        tangents: parseAttributeData(reifiedAttributeSet.tangents, "float3"),
        segments: parseAttributeData(reifiedAttributeSet.segments, "float"),
        scales: parseAttributeData(reifiedAttributeSet.scales, "float"),
        sectionIds: parseAttributeData(reifiedAttributeSet.sectionIds, "uint32"),
    }
}

function abstractCurveDataFromStandardCurveGeometryAttibuteSetData(curveAttributeSetData: StandardCurveGeometryAttributeSetData): AbstractCurveData {
    const attrRefDataSet = geometryAttributeSetDataToGeometryAttributeRefDataSet(curveAttributeSetData)
    const displayAttrRefSet = attributeRefSetFromGeometryAttributeRefDataSet(attrRefDataSet, "display")
    const renderAttrRefSet = attributeRefSetFromGeometryAttributeRefDataSet(attrRefDataSet, "render")
    const displayGeometryGraphResources = collectDisplayGraphResourcesFromGeometryAttributeRefDataSet(attrRefDataSet)

    const displayGeometryGraph = geomToCurve(displayAttrRefSet)
    const renderGeometryGraph = geomToCurve(renderAttrRefSet)

    return {
        contentHash: hashObject(graphToJson([displayGeometryGraph, renderGeometryGraph], NoopLogger)),
        displayGeometryGraph,
        renderGeometryGraph,
        displayGeometryGraphResources,
    }
}

function isCurveClosed(curveData: ReifiedCurveData): boolean {
    const firstPointIndex = 0
    const lastPointIndex = curveData.points.length / 3 - 1
    return (
        curveData.points[firstPointIndex * 3] === curveData.points[lastPointIndex * 3] &&
        curveData.points[firstPointIndex * 3 + 1] === curveData.points[lastPointIndex * 3 + 1] &&
        curveData.points[firstPointIndex * 3 + 2] === curveData.points[lastPointIndex * 3 + 2]
    )
}

export type GeometryGraphCurveParameters = {
    name: string
    curveAttributeSet: CurveGeometryAttributeSetLikeNode | null
} & ObjectNodeParameters

const definition: ObjectNodeDefinition<GeometryGraphCurveParameters> = {
    nodeClass: "GeometryGraphCurve",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                curveAttributeSet: z.nullable(curveGeometryAttributeSetLike),
            }),
            objectNodeParameters.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class GeometryGraphCurve extends CurveNode<GeometryGraphCurveParameters> {
    public override get definition() {
        return definition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<GeometryGraphCurveParameters> {
        if (!this.parameters.curveAttributeSet) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<GeometryGraphCurveParameters> {
        const {curveAttributeSet} = this.parameters
        if (!curveAttributeSet) return skipped

        const {evaluator} = context
        const {templateScope, templateContext} = evaluator
        const {sceneManager} = templateContext

        const scope = evaluator.getScope(this)

        const deferredCurveAttributeSetData = scope.waitForFinishedModifyingScene(evaluator.evaluate(curveAttributeSet), sceneManager)

        const [curveAttributeSetDataValid, curveAttributeSetDataInvalid] = scope.branch(
            scope.pureLambda(
                deferredCurveAttributeSetData,
                (curveAttributeSetData) => {
                    if (!curveAttributeSetData) return null
                    if (!isStandardCurveAttributeSetData(curveAttributeSetData)) return null
                    return curveAttributeSetData
                },
                "curveAttributeSetData",
            ),
        )

        const geometryGenerationData = scope.pureLambda(
            curveAttributeSetDataValid,
            (curveAttributeSetData) => setupGeometryGenerationData(curveAttributeSetData),
            "geometryGenerationData",
        )

        const geometryGenerationInputData = scope.get(geometryGenerationData, "input")

        const {completeGeometryData} = scope.node(GenerateGeometry, {
            sceneManager,
            inputGeometryData: geometryGenerationInputData,
        })

        const reifiedGeometryData = scope.get(completeGeometryData, "reified")
        const displayExprsTreeTokenSet = scope.get(scope.get(geometryGenerationData, "context"), "displayExprsTreeTokenSet")

        const abstractCurveData = scope.pureLambda(
            curveAttributeSetDataValid,
            (curveAttributeSetData) => abstractCurveDataFromStandardCurveGeometryAttibuteSetData(curveAttributeSetData),
            "abstractCurveData",
            {
                output: abstractCurveDataDesc,
            },
        )

        const reifiedCurveData = scope.pureLambda(
            scope.tuple(reifiedGeometryData, displayExprsTreeTokenSet),
            ([reifiedGeometryData, exprsTreeTokenSet]) => reifiedGeometryDataToReifiedCurveData(reifiedGeometryData, exprsTreeTokenSet),
            "reifiedCurveData",
            {
                output: reifiedCurveDataDesc,
            },
        )

        const isClosed = scope.pureLambda(reifiedCurveData, (reifiedCurveData) => isCurveClosed(reifiedCurveData), "isCurveClosed")

        const completeCurveData = scope.struct<CompleteCurveData>("CompleteCurveData", {
            abstract: abstractCurveData,
            reified: reifiedCurveData,
        })

        const curveData = scope.struct<CurveData>("CurveData", {
            type: "curve",
            visible: this.parameters.visible,
            controlPoints: EMPTY_LIST,
            closed: isClosed,
            completeCurveData,
            matrix: scope.value(Matrix4.identity(), TD.Matrix4, "matrix"),
        })

        templateScope.alias(scope.phi(curveData, curveAttributeSetDataInvalid), `curveData-${evaluator.getLocalId(this)}`)

        this.setupObject(
            scope,
            context,
            "GeometryGraphCurve",
            undefined,
            undefined,
            (_objectProps, _otherData) => scope.value(EMPTY_LIST, sceneNodeArrayDesc, "sceneNodes"),
            curveAttributeSetDataInvalid,
        )

        return visitNone(this.parameters)
    }
}
